import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../auth/AuthAllapot.tsx';
import { edgeHivas } from '../lib/edge.ts';
import { supabase } from '../lib/supabase.ts';
import { SZEREP_CIMKE } from '../lib/tipusok.ts';
import { Betolto, Fokonyv, Gomb, HibaUzenet, Mezo, OldalFej, Parbeszed, Ures } from '../ui/elemek.tsx';

interface Cel { userId: string; nev: string }

export default function SuperFelhasznalok() {
  const { session } = useAuth();
  const sajatId = session?.user.id;
  const [kereses, setKereses] = useState('');
  const [cel, setCel] = useState<Cel | null>(null);
  const [kesz, setKesz] = useState<string | null>(null);

  const lista = useQuery({
    queryKey: ['super-felhasznalok'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, email, nev, telefon, super_admin, created_at, tagsagok:office_members(szerep, aktiv, iroda:offices(nev))')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const visszaallit = useMutation({
    mutationFn: (userId: string) => edgeHivas('admin-mfa-visszaallitas', { userId }),
    onSuccess: () => { setKesz(`A kétlépcsős azonosítás visszaállítva: ${cel?.nev ?? ''}. Értesítést kapott.`); setCel(null); },
  });

  const szuro = kereses.trim().toLowerCase();
  const sorok = (lista.data ?? []).filter((p) => szuro === '' || (p.nev ?? '').toLowerCase().includes(szuro) || (p.email ?? '').toLowerCase().includes(szuro));

  return (
    <div className="grid gap-4">
      <OldalFej cim="Felhasználók" leiras="Meghívni az iroda „Tagok kezelése” oldaláról lehet: Irodák, majd az iroda, majd Tagok kezelése." />
      <Mezo label="Keresés névre vagy e-mailre" value={kereses} onChange={(e) => setKereses(e.target.value)} />
      <HibaUzenet hiba={lista.error} />
      {kesz && <p role="status" className="rounded-xl border border-siker px-3.5 py-2.5 text-sm text-siker">{kesz}</p>}
      {lista.isPending && <Betolto />}
      {lista.data && sorok.length === 0 && <Ures cim="Nincs találat" szoveg="Nincs a keresésnek megfelelő felhasználó." />}
      {sorok.length > 0 && (
        <Fokonyv>
          <ul className="divide-y divide-vonal">
            {sorok.map((p) => (
              <li key={p.user_id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                <div className="grid min-w-0 gap-1">
                  <span className="truncate font-medium">{p.nev || p.email}</span>
                  {p.nev && <span className="truncate text-sm text-szoveg-halk">{p.email}</span>}
                  <span className="flex flex-wrap gap-1.5">
                    {p.super_admin && <span className="rounded-[var(--sarok-kerek)] bg-kiemelt-halvany px-2.5 py-0.5 text-xs font-medium text-kiemelt">Super admin</span>}
                    {p.tagsagok.map((t, i) => (
                      <span key={i} className={`rounded-[var(--sarok-kerek)] border border-vonal-eros px-2.5 py-0.5 text-xs ${t.aktiv ? 'text-szoveg' : 'text-szoveg-halk line-through'}`}>
                        {t.iroda?.nev ?? 'Ismeretlen iroda'}: {SZEREP_CIMKE[t.szerep]}
                      </span>
                    ))}
                  </span>
                </div>
                {p.user_id !== sajatId && (
                  <Gomb valtozat="masodlagos" onClick={() => { setKesz(null); visszaallit.reset(); setCel({ userId: p.user_id, nev: p.nev || p.email || 'a felhasználó' }); }}>
                    Kétlépcsős azonosítás visszaállítása
                  </Gomb>
                )}
              </li>
            ))}
          </ul>
        </Fokonyv>
      )}

      <Parbeszed nyitva={cel !== null} cim="Kétlépcsős azonosítás visszaállítása" onBezar={() => setCel(null)}>
        <p>{cel?.nev} kétlépcsős tényezői törlődnek, a következő belépéskor újat kell beállítania. Értesítést kap róla.</p>
        <HibaUzenet hiba={visszaallit.error} />
        <div className="flex flex-wrap justify-end gap-3">
          <Gomb valtozat="masodlagos" onClick={() => setCel(null)}>Mégsem</Gomb>
          <Gomb valtozat="veszely" toltes={visszaallit.isPending} onClick={() => cel && visszaallit.mutate(cel.userId)}>Visszaállítás</Gomb>
        </div>
      </Parbeszed>
    </div>
  );
}
